import type { Category } from "@/lib/spots";

export type MarkName = Category | "fish" | "palm" | "wave" | "surf" | "sun" | "pin" | "map" | "heart" | "surprise";

// Single-line ink drawings in the loose marker style of the moodboard.
// Drawn here as paths. Never an icon font, never a stock set.
const PATHS: Record<MarkName, string> = {
  coffee: `M13 26c-.4 9 2.6 20.4 6.8 24.1 3.9 3.4 13.6 3.6 17.7.3C42 47 45.3 35.6 45.1 26c-8.2-1.5-24-1.6-32.1 0Z
    M45.6 30.6c4.6-1.6 8.6.4 8.4 4.6-.2 4.3-4.2 6.3-8.2 5.4
    M24.6 16.4c1.8-2.3-1.4-4.2.3-6.6
    M33.4 16.1c1.9-2.5-1.5-4.4.2-6.9`,
  drinks: `M12.5 15.5c3.6 9.9 9.9 17.6 16.4 17.7 6.6.1 13.3-7.7 17-17.8-10.6-1.4-22.6-1.5-33.4.1Z
    M29 33.4V47
    M20.4 50.6c4.9-1.7 12.4-1.8 17.5-.2
    M25.2 21.6a2.6 2.6 0 1 0 .1-.1`,
  food: `M9.5 30c0 8.6 7.9 15.4 17.4 15.4S44.4 38.6 44.4 30c0-8.5-7.9-15.3-17.5-15.3S9.5 21.5 9.5 30Z
    M16.4 30.2c0 5 4.9 9 10.6 9s10.5-4 10.5-9
    M51 13.8v14.6c0 2.2-1.5 3.3-3.4 3.3s-3.4-1.1-3.4-3.3V13.9
    M47.7 31.7v16.8`,
  // The wildcard's mark. A headland with the sun going down behind it, which is what most
  // of these actually are.
  outdoors: `M4 46c7.5-11.5 13.5-17 18-16.5 4.5.5 9 7 13.5 12.5 3-4.5 6-7 9-7.5 4.5-.7 9.5 3.2 14.5 11.5Z
    M40 20a9 9 0 1 0 .1 0Z
    M4 52.5c7-2.4 14-2.4 21 0 7-2.4 14-2.4 21 0`,
  services: `M17.6 13.2 40.4 43.7
    M42.5 13.4 19.3 44
    M18.6 44.4a5.6 5.6 0 1 0-.1-.1Z
    M41.2 44.2a5.6 5.6 0 1 0-.1-.1Z`,
  shop: `M6 28.5c2.6-8.2 6.8-13.6 11.2-13.8 8.8-.5 17.2-.5 26 0 4.4.2 8.6 5.6 11.2 13.8Z
    M6 28.5c2.4 3.4 4.9 3.4 7.3 0 2.4 3.4 4.9 3.4 7.3 0 2.4 3.4 4.9 3.4 7.3 0 2.4 3.4 4.9 3.4 7.3 0 2.4 3.4 4.9 3.4 7.3 0 2.4 3.4 4.9 3.4 7.9 0
    M13 33.5v17.5 M49 33.5v17.5 M13 51h36 M24 51V40h14v11`,
  fish: `M8.5 30c7-9.4 17.6-14 26.4-13.7 7.4.3 13.6 4.7 17.6 13.7-4 9-10.2 13.4-17.6 13.7C26.1 44 15.5 39.4 8.5 30Z
    M52.5 30c1.7-3.3 4.4-5.4 6.6-5.2-1.2 3.4-1.2 7 0 10.4-2.2.2-4.9-1.9-6.6-5.2
    M18.6 26.4a2.3 2.3 0 1 0 .1-.1
    M30 22.4c3.7 4.8 3.7 10.4 0 15.2`,
  palm: `M29 54c-1.6-13-1-22.6 2-30.8
    M31 23.2c-6-6-13-8-21-5.4 5.3-5.2 12.4-5.8 21-2
    M31 23.2c6.3-5.6 13.6-7.2 21.4-4.2-5.6-5-12.8-5.2-21.4-1.2
    M31 23.2c-2.8-7.8-2.2-14.8 1.6-20.8 3.4 6.2 3.4 13.2 0 20.8
    M31 23.2c7 1.4 12.4 5.4 15.4 11.6-6-3.6-11.4-4.6-15.4-3.2
    M22.5 54c4.6-1.4 9.6-1.4 14 0`,
  wave: `M4 38.5c5.4-5.2 10.7-5.2 16 0 5.3 5.2 10.7 5.2 16 0 5.3-5.2 10.6-5.2 16 0
    M4 48.5c5.4-5.2 10.7-5.2 16 0 5.3 5.2 10.7 5.2 16 0 5.3-5.2 10.6-5.2 16 0
    M14 28c6.6-13.4 17.6-19 33-16.6-8.8 2.6-14.8 7.6-18 15`,
  surf: `M31 6c9.4 10.4 13.6 21.4 12.6 33C42.6 48.6 38.4 55 31 58c-7.4-3-11.6-9.4-12.6-19C17.4 27.4 21.6 16.4 31 6Z
    M31 12.5v39
    M23 24.5c5.4 3 10.6 3 16 0
    M23 40.5c5.4 3 10.6 3 16 0`,
  sun: `M31 20a11 11 0 1 0 .1 0Z
    M31 4.5v7 M31 52.5v7 M46.5 31h7 M8.5 31h7
    M42.4 19.6l4.9-4.9 M14.7 47.3l4.9-4.9 M42.4 42.4l4.9 4.9 M14.7 14.7l4.9 4.9`,
  pin: `M31 57c9.6-13.4 14.4-23.4 14.4-30C45.4 17.6 39 11 31 11s-14.4 6.6-14.4 16c0 6.6 4.8 16.6 14.4 30Z
    M31 33.5a6.5 6.5 0 1 0 .1 0Z`,
  map: `M4 15.5 21 9l20 6.5L58 9v37.5L41 53l-20-6.5L4 53Z
    M21 9v37.5 M41 15.5V53`,
  // Off-centre and slightly lopsided, like the rest of them. A heart drawn with a ruler
  // reads as an icon someone downloaded.
  heart: `M31 53.5C15.5 42.8 7.5 33.6 7.5 24.8 7.5 17.4 13.2 11.5 20.3 11.5c4.6 0 8.7 2.6 10.9 6.7
    2.3-4.1 6.4-6.7 11-6.7 7.1 0 12.8 5.9 12.8 13.3 0 8.8-8.2 18-24 28.7Z`,
  // Two sparkles, the second smaller and shoved into the corner. A die would have been the
  // obvious drawing and it would have promised a gamble, which is the wrong feeling: this
  // is the app choosing for you, not you rolling for it.
  surprise: `M26 7c2.5 9.2 6.6 14.3 14 16.8-7.4 2.4-11.5 7.5-14 17-2.4-9.5-6.5-14.6-13.9-17C19.5 21.3 23.6 16.2 26 7Z
    M46.5 34c1.3 4.7 3.4 7.3 7.3 8.6-3.9 1.2-6 3.9-7.3 8.8-1.2-4.9-3.3-7.6-7.2-8.8 3.9-1.3 6-3.9 7.2-8.6Z
    M14 47.5a3.2 3.2 0 1 0 .1 0Z`,
};

const LABELS: Record<MarkName, string> = {
  coffee: "Coffee",
  drinks: "Drinks",
  food: "Food",
  services: "Barbers and studios",
  shop: "Shop",
  outdoors: "Outside",
  fish: "Fish",
  palm: "Palm",
  wave: "Waves",
  surf: "Surfboard",
  sun: "Sun",
  pin: "Pin",
  map: "Map",
  heart: "Saved",
  surprise: "Surprise me",
};

type Props = {
  name: MarkName;
  size?: number;
  color?: string;
  decorative?: boolean;
  className?: string;
};

export function Mark({ name, size = 48, color = "var(--blue)", decorative = false, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 62 62"
      fill="none"
      stroke={color}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : LABELS[name]}
      className={className}
      style={{ overflow: "visible" }}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
